'use client'

import type { FunctionComponent, FormEventHandler } from 'react'
import { useCallback, useRef, useState, useEffect } from 'react'
import type { MailingListLocaleResult } from '../data/remote/MailingListFragment'
import { api } from '../utilities/api'
import { useMirrorLoading } from 'shared-loading-indicator'
import { useSite } from './contexts/SiteContextProvider'
import { useTranslate } from './contexts/TranslationsContextProvider'
import { Input } from './Input'
import { RichTextRendererWithElements } from './RichTextRendererWithElements'
import styles from './FrontPageSideBanner.module.sass'
import Image from 'next/image'
import { RichTextRenderer } from '@contember/react-client'

type Props = {
    list: MailingListLocaleResult
}

export const FrontPageSideBanner: FunctionComponent<Props> = ({ list }) => {
    const translation = useTranslate()
    const { siteCode } = useSite()

    const form = useRef<HTMLFormElement>(null)
    const [email, setEmail] = useState('')
    const [emailError, setEmailError] = useState<string | null>(null)

    const BrevoData = list?.mailingList?.brevoData
    const mailingListId = list?.id

    const mutation = api.subscribeOnMailingList.send.useMutation()
    const isPending = mutation.isPending
    useMirrorLoading(isPending)

    const subscribe = useCallback<FormEventHandler<HTMLFormElement>>(
        (event) => {
            event.preventDefault()
            if (!email || email.trim() === '') {
                const msg = siteCode === 'pl' ? 'Proszę podać e‑mail.' : (siteCode === 'cz' || siteCode === 'sk') ? 'Prosím, vyplňte e‑mail.' : 'Please enter your email.'
                setEmailError(msg)
                return
            }
            setEmailError(null)
            if (!BrevoData || !mailingListId) return
            mutation.mutate({ siteCode, mailingListId, brevoListId: BrevoData.listId, email })
        },
        [BrevoData, email, mailingListId, mutation, siteCode],
    )

    useEffect(() => {
        if (mutation.data?.status === 'success') {
            form.current?.reset()
            setEmail('')
            setEmailError(null)
        }
    }, [mutation.data?.status])

    const accent = list.sideBannerAccentColor ?? list.bannerAccentColor ?? ''
    const title = list.sideBannerTitle ?? list.bannerTitle ?? ''

    return (
        <div className={styles.main} style={{ '--bannerAccentColor': accent } as React.CSSProperties}>
            <div className={styles.content}>
                <div style={{ alignSelf: 'flex-end' }}>
                    <Image src="/images/alma-logo.svg" alt="Alma logo" width={100} height={40} priority />
                </div>
                {title && (
                    <h3 className={styles.title}>
                        <RichTextRendererWithElements source={title} />
                    </h3>
                )}

                <form ref={form} onSubmit={subscribe} className={styles.form}>
                    <div className={styles.emailRow}>
                        <Input
                            type="email"
                            // required
                            placeholder={translation('newsletterPopup.emailPlaceholder')}
                            value={email}
                            onChange={(e) => {
                                setEmail(e.target.value)
                                if (e.target.value && e.target.value.trim() !== '') setEmailError(null)
                            }}
                            backgroundColor="orange"
                        />
                        {emailError && <div className={styles.errorNote}>{emailError}</div>}
                    </div>
                    <button type="submit" className={styles.sendButton} disabled={isPending}>
                        {translation('newsletterPopup.button.label')}
                    </button>
                    {mutation.data?.status !== undefined && (
                        <div className={styles.note}>
                            {mutation.data.status === 'success'
                                ? translation('newsletter.subscription.success')
                                : mutation.data.errorResponse && mutation.data.errorResponse.code === 'duplicate_parameter'
                                    ? translation('newsletter.subscription.duplicate_parameter')
                                    : translation('newsletter.subscription.error')}
                        </div>
                    )}
                    {list.sideBannerImage?.url && <div className={styles.illustration}>
                        <Image
                            src={list.sideBannerImage?.url}
                            alt={list.sideBannerImage?.alt ?? ''}
                            width={500}
                            height={300}
                            priority
                        />
                    </div>}
                    {list.sideBannerNote && <div className={styles.note}><RichTextRenderer source={list.sideBannerNote ?? ''} /></div>}
                </form>
            </div>
        </div>
    )
}


